import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Truck, Plus, AlertTriangle, Search, RefreshCw } from 'lucide-react';
import { api } from '../../api/client';
import { EOtpremnicaSettingsPanel } from './EOtpremnicaSettingsPanel';

type Tenant = { id: string; name: string };

type Document = {
  id: string;
  documentType: string;
  role: string;
  direction: string;
  documentNumber: string;
  localStatus: string;
  sefStatus: string | null;
  sefDocumentId: string | null;
  issueDate: string | null;
  despatchTypeCode: string | null;
  supplierName: string | null;
  supplierTaxId: string | null;
  customerName: string | null;
  customerTaxId: string | null;
  createdAt: string;
};

const tabs = ['Izlazne', 'Ulazne', 'Podešavanja'] as const;
type Tab = (typeof tabs)[number];

// Mapa status → (boja, tekst)
const supplierStatusDisplay: Record<string, { bg: string; color: string; label: string }> = {
  Sent: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa', label: 'Poslato' },
  Cancelled: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af', label: 'Stornirano' },
  Delivered: { bg: 'rgba(34,197,94,0.2)', color: '#4ade80', label: 'Fizički prijem' },
  Seized: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5', label: 'Zaplenjeno' },
  Fulfilled: { bg: 'rgba(34,197,94,0.25)', color: '#86efac', label: 'Usaglašeno' },
};

const localStatusDisplay: Record<string, { bg: string; color: string; label: string }> = {
  draft: { bg: 'rgba(156,163,175,0.2)', color: '#9ca3af', label: 'Nacrt' },
  validating: { bg: 'rgba(234,179,8,0.2)', color: '#facc15', label: 'Validacija' },
  validation_failed: { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5', label: 'Validacija pala' },
  queued: { bg: 'rgba(59,130,246,0.15)', color: '#60a5fa', label: 'U redu' },
  submitting: { bg: 'rgba(59,130,246,0.2)', color: '#93c5fd', label: 'Šalje se' },
  submit_failed: { bg: 'rgba(239,68,68,0.25)', color: '#fca5a5', label: 'Slanje palo' },
  submitted: { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa', label: 'Poslato (čeka SEF)' },
  failed: { bg: 'rgba(239,68,68,0.3)', color: '#fca5a5', label: 'Neuspešno' },
};

export function EOtpremnicaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab: Tab = tabParam === 'inbound' ? 'Ulazne' : tabParam === 'settings' ? 'Podešavanja' : 'Izlazne';

  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => {
      setTenants(rows);
      if (rows.length > 0) setSelectedTenantId(rows[0].id);
    }).catch(() => {});
  }, []);

  const fetchDocuments = useCallback(async () => {
    if (!selectedTenantId) return;
    const direction = activeTab === 'Izlazne' ? 'outbound' : 'inbound';
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<Document[]>(`/admin/eotpremnica/documents?tenantId=${selectedTenantId}&direction=${direction}`);
      setDocuments(data);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, activeTab]);

  useEffect(() => {
    if (activeTab !== 'Podešavanja' && selectedTenantId) fetchDocuments();
  }, [activeTab, selectedTenantId, fetchDocuments]);

  const formatDate = (d: string | null) => d ? new Date(d).toLocaleDateString('sr-Latn-RS') : '-';

  const statusBadge = (doc: Document) => {
    // Ako imamo sefStatus, on je autoritativan; else prikaži localStatus
    if (doc.sefStatus) {
      const sc = supplierStatusDisplay[doc.sefStatus];
      if (sc) {
        return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: sc.bg, color: sc.color, fontWeight: 700 }}>{sc.label}</span>;
      }
      return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: 'rgba(156,163,175,0.2)', color: '#9ca3af', fontWeight: 700 }}>{doc.sefStatus}</span>;
    }
    const ls = localStatusDisplay[doc.localStatus] || localStatusDisplay.draft;
    return <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: ls.bg, color: ls.color, fontWeight: 700 }}>{ls.label}</span>;
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>eOtpremnica</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Centralni Registar eOtpremnica - MFin RS
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {activeTab === 'Izlazne' && (
          <button onClick={() => navigate('/eotpremnica/despatch-advices/new')} style={btnPrimary}>
            <Plus size={14} /> Nova otpremnica
          </button>
        )}
      </header>

      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '1px solid var(--color-border)' , overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: activeTab === tab ? 600 : 500,
              color: activeTab === tab ? 'var(--color-primary)' : 'var(--color-text-muted)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
              cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
              marginBottom: -1,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {(activeTab === 'Izlazne' || activeTab === 'Ulazne') && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>Tenant:</label>
            <select value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)} style={selectStyle}>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button onClick={fetchDocuments} disabled={loading} style={btnSecondary}>
              <RefreshCw size={14} /> {loading ? '...' : 'Osveži'}
            </button>
          </div>

          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}

          <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border)' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--color-bg)' }}>
                    <th style={th}>Broj</th>
                    <th style={th}>Tip</th>
                    <th style={th}>{activeTab === 'Izlazne' ? 'Primalac' : 'Pošiljalac'}</th>
                    <th style={th}>PIB</th>
                    <th style={th}>Status</th>
                    <th style={th}>Datum</th>
                    <th style={{ ...th, width: 60 }}>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && documents.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ ...td, textAlign: 'center', padding: 40, color: 'var(--color-text-muted)' }}>
                        <Truck size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
                        <div>Nema dokumenata</div>
                      </td>
                    </tr>
                  )}
                  {documents.map((d) => {
                    const counterparty = activeTab === 'Izlazne'
                      ? { name: d.customerName, pib: d.customerTaxId }
                      : { name: d.supplierName, pib: d.supplierTaxId };
                    return (
                      <tr key={d.id} onClick={() => navigate(`/eotpremnica/documents/${d.id}`)} style={{ cursor: 'pointer' }}>
                        <td style={{ ...td, color: 'var(--color-text-strong)', fontWeight: 600 }}>{d.documentNumber}</td>
                        <td style={{ ...td, color: 'var(--color-text-muted)' }}>
                          {d.documentType === 'despatch_advice' ? 'Otpremnica' : d.documentType === 'receipt_advice' ? 'Prijemnica' : 'Akcija'}
                          {d.despatchTypeCode && ` (${d.despatchTypeCode})`}
                        </td>
                        <td style={{ ...td, color: 'var(--color-text-strong)' }}>{counterparty.name || '-'}</td>
                        <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{counterparty.pib || '-'}</td>
                        <td style={td}>{statusBadge(d)}</td>
                        <td style={{ ...td, color: 'var(--color-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{formatDate(d.issueDate)}</td>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => navigate(`/eotpremnica/documents/${d.id}`)} style={iconBtn} title="Otvori">
                            <Search size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'Podešavanja' && <EOtpremnicaSettingsPanel />}
    </div>
  );
}

const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', color: 'var(--color-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const selectStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-bg-surface)', color: 'var(--color-text)', minWidth: 200 };
